import { useId, useState } from "react";

import { errorMessage, useConsoleConfig, useUpdateMe } from "../api/queries";
import { useSession } from "../auth/SessionProvider";
import { ConsoleKeySection } from "../components/account/ConsoleKeySection";
import { SecondFactorSection } from "../components/account/SecondFactorSection";
import { PageHeader } from "../components/layout/PageHeader";
import { Field } from "../components/ui/Field";

/**
 * Your account: your name, your keys, and your code.
 *
 * There is no SSH key here and no infrastructure credential of any kind: this
 * console hands out links with a clock on them, never a shell and never a
 * bucket key.
 *
 * Returns:
 *     The account page.
 */
export function AccountPage(): JSX.Element {
  const { me } = useSession();
  const config = useConsoleConfig();
  const nameId = useId();
  const [displayName, setDisplayName] = useState(me.display_name ?? "");
  const update = useUpdateMe();

  return (
    <>
      <PageHeader
        eyebrow="Your account"
        title={me.display_name ?? me.username}
        subtitle={me.email}
      />

      <section className="section">
        <div className="section__head">
          <h2 className="section__title">What to call you</h2>
        </div>
        <p className="section__lead">
          The one thing on this page you own. Who you are is the Google address
          above — that is what every file you add and every read you make is
          recorded against, and it is not editable anywhere.
        </p>
        <form
          className="form"
          onSubmit={(event) => {
            event.preventDefault();
            update.mutate({
              display_name: displayName.trim() ? displayName.trim() : null,
            });
          }}
        >
          <Field
            label="Your name"
            htmlFor={nameId}
            error={update.isError ? errorMessage(update.error) : undefined}
          >
            <input
              id={nameId}
              className="input"
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
            />
          </Field>
          <button
            type="submit"
            className="btn btn--sm"
            disabled={update.isPending}
          >
            {update.isPending ? "Saving…" : "Save"}
          </button>
        </form>
      </section>

      <ConsoleKeySection config={config.data} />

      <SecondFactorSection
        secondFactor={me.second_factor}
        stepUpMinutes={config.data?.step_up_minutes ?? null}
      />

      {config.data ? (
        <section className="section">
          <div className="section__head">
            <h2 className="section__title">What this console claims about you</h2>
          </div>
          <p className="section__lead">{config.data.sso.note}</p>
        </section>
      ) : null}
    </>
  );
}
